import axios from 'axios';
import globalData from '../../config/global_variables';
import {
  daysToMonths,
  getOperatorValueKind,
  isMonthCondition,
  monthsToDays,
} from './steps/EligibilityConditions/conditionOperators';

const baseUrl =  globalData.MEMBERSHIP_ENGINE_URL;
const authToken = () => {
  try {
    return JSON.parse(localStorage.userData || '{}').token || '';
  } catch {
    return '';
  }
};

const headers = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${authToken()}`,
});

// customer_profiles columns that Attribute Conditions can use (public endpoint, no auth).
// TODO: switch to `${baseUrl}/api/v1/customers/fields` once the backend route is deployed.
const CONDITION_FIELDS_URL = `${baseUrl}/api/v1/customers/fields`;
let conditionFieldsRequest = null;

// Fetched once and shared by every condition row; a failed request is retried next time.
export const fetchConditionFields = () => {
  if (!conditionFieldsRequest) {
    conditionFieldsRequest = axios.get(CONDITION_FIELDS_URL)
      .then((res) => {
        const list = res?.data?.data || res?.data || [];
        return Array.isArray(list) ? list.filter((item) => item?.field) : [];
      })
      .catch((err) => {
        conditionFieldsRequest = null;
        throw err;
      });
  }
  return conditionFieldsRequest;
};

// Lightweight product search (id/sku/name/is_visible only) used to search SKUs for
// Gift SKUs / Included SKUs instead of free-typing them. Cursor-paginated for infinite scroll.
export const fetchMembershipProductsLite = async ({ storeLabel, search, cursor, limit = 50 } = {}) => {
  if (!storeLabel) return { items: [], nextCursor: null, hasMore: false };

  const res = await axios.get(globalData.GET_MEMBERSHIP_PRODUCTS_LITE, {
    headers: headers(),
    params: {
      store_label: storeLabel,
      ...(search && search.trim().length >= 2 ? { search: search.trim() } : {}),
      ...(cursor ? { cursor } : {}),
      limit,
    },
  });
  return {
    items: res?.data?.data || [],
    nextCursor: res?.data?.nextCursor ?? null,
    hasMore: Boolean(res?.data?.hasMore),
  };
};

// Resolves {id, sku, name, is_visible} for a known list of SKUs in a single request
// (backend max 500 SKUs per call) — used by Review & Publish to show product names
// without one search request per SKU.
const MEMBERSHIP_PRODUCTS_BULK_CHUNK_SIZE = 500;

export const fetchMembershipProductsBulk = async ({ storeLabel, skus = [] } = {}) => {
  const uniqueSkus = [...new Set(skus.filter(Boolean))];
  if (!storeLabel || !uniqueSkus.length) return [];

  const chunks = [];
  for (let i = 0; i < uniqueSkus.length; i += MEMBERSHIP_PRODUCTS_BULK_CHUNK_SIZE) {
    chunks.push(uniqueSkus.slice(i, i + MEMBERSHIP_PRODUCTS_BULK_CHUNK_SIZE));
  }

  const results = await Promise.all(
    chunks.map((chunk) =>
      axios.post(
        globalData.GET_MEMBERSHIP_PRODUCTS_BULK,
        { store_label: storeLabel, skus: chunk },
        { headers: headers() }
      ).then((res) => res?.data?.data || [])
    )
  );

  return results.flat();
};

export const fetchMembershipRules = async (params = {}) => {
  const res = await axios.get(`${baseUrl}/api/v1/rules/listing`, {
    headers: headers(),
    params,
  });
  return res?.data?.data || res?.data || [];
};

export const duplicateMembershipRule = async (ruleId, ruleName) => {
  const res = await axios.post(
    `${baseUrl}/api/v1/rules/membership-rules/${encodeURIComponent(ruleId)}/duplicate`,
    ruleName ? { rule_name: ruleName } : {},
    { headers: headers() }
  );
  return res?.data?.data || res?.data || null;
};

export const updateMembershipRuleStatus = async (ruleId, status) => {
  const res = await axios.patch(
    `${baseUrl}/api/v1/rules/membership-rules/${encodeURIComponent(ruleId)}/status`,
    { status },
    { headers: headers() }
  );
  return res?.data?.data || res?.data || null;
};

// Wizard form fields -> create-rule API request body.
const toNumber = (value) => {
  const num = Number(String(value ?? '').trim());
  return String(value ?? '').trim() !== '' && Number.isFinite(num) ? num : value;
};

const getBenefitMechanic = ({ giftEnabled, discountEnabled }) => {
  if (giftEnabled && discountEnabled) return 'gift_discount';
  if (giftEnabled) return 'gift';
  if (discountEnabled) return 'discount';
  return undefined;
};

// Condition inputs are free text; send true/false and numbers with their real types.
const parseConditionValue = (value) => {
  const text = String(value ?? '').trim();
  if (text.toLowerCase() === 'true') return true;
  if (text.toLowerCase() === 'false') return false;
  if (text !== '' && Number.isFinite(Number(text))) return Number(text);
  return text;
};

// UI group -> API { logic, items | conditions }. Top-level rows use `condition_name`,
// rows inside nested groups use `field`, matching the rules API shape.
const mapGroupToConditions = (group = {}, isRoot = false) => {
  const nameKey = isRoot ? 'condition_name' : 'field';
  const toRow = ({ field, operator, value }) => {
    const kind = getOperatorValueKind(operator);
    let apiValue;
    if (kind === 'none') apiValue = null;
    // is_one_of keeps the typed values as strings.
    else if (kind === 'list') apiValue = (Array.isArray(value) ? value : []).map((item) => String(item).trim()).filter(Boolean);
    else if (kind === 'between') apiValue = (Array.isArray(value) ? value : []).map(parseConditionValue);
    // Entered in months, sent as days.
    else if (isMonthCondition(field, operator) && String(value ?? '').trim() !== '') apiValue = monthsToDays(value);
    else apiValue = parseConditionValue(value);
    return { [nameKey]: field, operator, value: apiValue };
  };
  const entries = [
    ...(group.conditions || []).map(toRow),
    ...(group.children || []).map((child) => mapGroupToConditions(child)),
    ...(group.afterConditions || []).map(toRow),
  ];
  const logic = String(group.type || 'And').toUpperCase();

  return isRoot ? { items: entries, logic } : { logic, conditions: entries };
};

const mapEligibilityGroupsToConditions = (groups = []) => {
  if (!Array.isArray(groups) || !groups.length) return null;
  if (groups.length === 1) return mapGroupToConditions(groups[0], true);
  return { items: groups.map((group) => mapGroupToConditions(group)), logic: 'AND' };
};

// API conditions -> UI eligibilityGroups (used when opening a rule for Edit/View).
const mapConditionsToEligibilityGroups = (conditions) => {
  if (!conditions || typeof conditions !== 'object' || Array.isArray(conditions)) return undefined;
  let nextId = 0;
  const makeId = () => `api-${Date.now()}-${(nextId += 1)}`;

  const toGroup = (node) => {
    const groupId = makeId();
    const group = { id: groupId, type: String(node.logic || 'AND').toUpperCase() === 'OR' ? 'Or' : 'And', conditions: [], afterConditions: [], children: [] };
    (node.items || node.conditions || []).forEach((entry) => {
      if (entry && (entry.items || entry.conditions)) {
        group.children.push(toGroup(entry));
        return;
      }
      const field = entry?.condition_name ?? entry?.field;
      const operator = entry?.operator || '=';
      const hasValue = entry?.value !== null && entry?.value !== undefined && entry?.value !== '';
      let value = '';
      if (Array.isArray(entry?.value)) value = entry.value.map((item) => String(item));
      // API stores days; the form shows months for these fields.
      else if (hasValue && isMonthCondition(field, operator)) value = daysToMonths(entry.value);
      else if (hasValue) value = String(entry.value);
      const row = {
        id: `${groupId}-c${group.conditions.length + group.afterConditions.length + 1}`,
        field,
        operator,
        value,
      };
      // Rows after a nested group render below it, same as the wizard's afterConditions.
      (group.children.length ? group.afterConditions : group.conditions).push(row);
    });
    return group;
  };

  return [toGroup(conditions)];
};

export const mapFormDataToRulePayload = (formData = {}) => {
  const payload = {
    rule_name: formData.ruleName?.trim(),
    storefront: formData.storefront,
    short_desc: formData.ruleDescription?.trim(),
    benefit_mechanic: getBenefitMechanic(formData),
  };

  // Gift fields are only filled in (and validated) while the Gift toggle is on.
  if (formData.giftEnabled) {
    const giftSkus = Array.isArray(formData.giftSkus) ? formData.giftSkus : [formData.giftSkus].filter(Boolean);
    payload.threshold_min = toNumber(formData.minCartValue);
    payload.gifts = giftSkus.map((sku) => ({ sku: String(sku).trim() }));
    payload.gifts_per_rule = toNumber(formData.giftsPerOrder);
  }

  if (formData.discountEnabled) {
    // API accepts 'percentage' or 'fixed_amount'; the form option is 'fixed'.
    payload.discount_type = formData.discountType === 'fixed' ? 'fixed_amount' : formData.discountType;
    payload.discount_value = toNumber(formData.discountValue);
  }

  const isLifecycle = formData.ruleType === 'Lifecycle Triggered';
  const triggerWindow = formData.triggerWindows?.[0] || {};
  const lifecycleDays = String(triggerWindow.days ?? formData.triggerWindowDays ?? '').trim();
  const redemptionLimit = String(formData.redemptionLimit ?? '').trim();
  const isUnlimited = Boolean(formData.redemptionLimitUnlimited);
  // Inclusion and exclusion are sent independently; an empty list is sent as null.
  const toProductList = (skus) => {
    const productSkus = (Array.isArray(skus) ? skus : [])
      .map((sku) => String(sku).trim())
      .filter(Boolean);
    return productSkus.length ? { product_skus: productSkus } : null;
  };
  const startDate = !isLifecycle && formData.activeStartDate ? formData.activeStartDate : null;
  const endDate = !isLifecycle && formData.activeEndDate ? formData.activeEndDate : null;

  return {
    ...payload,
    area: isLifecycle ? 'lifecycle_triggered' : 'bau',
    benefit_type: formData.memberType === 'Prive' ? 'prive' : 'member',
    // Active period only applies to BAU rules; both dates are optional.
    active_window_start_date: startDate,
    active_window_end_date: endDate,
    activation_type: startDate && endDate ? 'scheduled' : 'always_on',
    redemption_limit_type: isUnlimited ? 'unlimited' : 'per_customer',
    redemption_limit: isUnlimited || !redemptionLimit ? null : Number(redemptionLimit),
    priority: toNumber(formData.priority),
    conditions: mapEligibilityGroupsToConditions(formData.eligibilityGroups),
    lifecycle_window_days: isLifecycle && lifecycleDays ? Number(lifecycleDays) : null,
    lifecycle_type: isLifecycle ? String(triggerWindow.field || 'Welcome').toLowerCase() : null,
    lifecycle_redemption_type: isLifecycle
      ? (formData.RedemptionType === 'Once Per Year' ? 'once_per_year' : 'one_time')
      : null,
    inclusion: toProductList(formData.includedSkus),
    exclusion: toProductList(formData.excludedSkus),
  };
};

// Pass extra body fields such as { status: 'draft' } for Save as Draft.
export const createMembershipRule = async (formData, extraFields = {}) => {
  const res = await axios.post(`${baseUrl}/api/v1/rules/create`, { ...mapFormDataToRulePayload(formData), ...extraFields }, {
    headers: headers(),
  });
  return res?.data;
};

const ruleUrl = (ruleId) => `${baseUrl}/api/v1/rules/membership-rules/${encodeURIComponent(String(ruleId))}`;

export const fetchMembershipRuleById = async (ruleId) => {
  const res = await axios.get(ruleUrl(ruleId), { headers: headers() });
  return res?.data?.data || null;
};

// Same body as create; used by Save as Draft / Publish Rule while editing.
export const updateMembershipRule = async (ruleId, formData, extraFields = {}) => {
  const res = await axios.put(ruleUrl(ruleId), { ...mapFormDataToRulePayload(formData), ...extraFields }, {
    headers: headers(),
  });
  return res?.data;
};
// export const staticData =() [
//   {
//     rule_id: '1',
//     rule_name: 'Birthday Gold 7',
//     rule_description: '#1007 - by suraj',
//     benefit_mechanic: 'prive',
//     mechanic: 'Discount + Gift',
//     area: 'BAU',
//     redemption_limit_type: 'per_customer',
//     redemption_limit: 1,
//     storefront: 'DE',
//     priority: 1,
//     status: 'Active',
//     active_window_start_date: '2025-12-28',
//     active_window_end_date: '2025-12-28',    
//     created_at: '2025-12-28',
//     updated_at: '2025-12-28',
//   },
//   {
//     rule_id: '2',
//     rule_name: 'Black Friday Discount 12',
//     rule_description: '#1012 - by admin',
//     benefit_mechanic: 'Member',
//     mechanic: 'Discount',
//     area: 'Lifecycle Triggered',
//     redemption_limit_type: 'per_customer',
//     redemption_limit: 1,
//     storefront: 'UK',
//     priority: 4,
//     status: 'Draft',
//     active_window_start_date: '2025-12-28',
//     active_window_end_date: '2025-12-28',   
//     created_at: '2025-12-28',
//     updated_at: '2025-12-28',
//   },
//   {
//     rule_id: '3',
//     rule_name: 'Welcome Gift 3',
//     rule_description: '#1003 - by priya.k',
//     benefit_mechanic: 'prive',
//     mechanic: 'Gift',
//     area: 'BAU',
//     redemption_limit_type: 'per_customer',
//     redemption_limit: 1,
//     storefront: 'DE',
//     priority: 8,
//     status: 'Archived',
//     active_window_start_date: '2025-12-28',
//     active_window_end_date: '2025-12-28',    
//     created_at: '2025-12-28',
//     updated_at: '2025-12-28',
//   },
//   {
//     rule_id: '4',
//     rule_name: 'VIP Spactive_window_end_date Bonus 21',
//     rule_description: '#1021 - by rahul.m',
//     benefit_mechanic: 'prive',
//     mechanic: 'Discount + Gift',
//     area: 'Lifecycle Triggered',
//     redemption_limit_type: 'per_customer',
//     redemption_limit: 1,
//     storefront: 'UK',
//     priority: 2,
//     status: 'Paused',
//     active_window_start_date: '2025-12-28',
//     active_window_end_date: '2025-12-28',    
//     created_at: '2025-12-28',
//     updated_at: '2025-12-28',
//   },
//   {
//     rule_id: '5',
//     rule_name: 'Cart Abandon Recovery 9',
//     rule_description: '#1009 - by suraj',
//     benefit_mechanic: 'Member',
//     mechanic: 'Discount + Gift',
//     area: 'Lifecycle Triggered',
//     redemption_limit_type: 'per_customer',
//     redemption_limit: 1,
//     storefront: 'US',
//     priority: 3,
//     status: 'Draft',
//     active_window_start_date: '2025-12-28',
//     active_window_end_date: '2025-12-28',   
//     created_at: '2025-12-28',
//     updated_at: '2025-12-28',
//   },
//   {
//     rule_id: '6',
//     rule_name: 'First Purchase GWP 15',
//     rule_description: '#1015 - by admin',
//     benefit_mechanic: 'Member',
//     mechanic: 'Gift',
//     area: 'BAU',
//     redemption_limit_type: 'per_customer',
//     redemption_limit: 1,
//     storefront: 'DE',
//     priority: 5,
//     status: 'Active',
//     active_window_start_date: '2025-12-28',
//     active_window_end_date: '2025-12-28',   
//     created_at: '2025-12-28',
//     updated_at: '2025-12-28',
//   },
//   {
//     rule_id: '7',
//     rule_name: 'VIP Spactive_window_end_date Bonus 21',
//     rule_description: '#1021 - by rahul.m',
//     benefit_mechanic: 'prive',
//     mechanic: 'Discount + Gift',
//     area: 'Lifecycle Triggered',
//     redemption_limit_type: 'per_customer',
//     redemption_limit: 1,
//     storefront: 'UK',
//     priority: 2,
//     status: 'Paused',
//     active_window_start_date: '2025-12-28',
//     active_window_end_date: '2025-12-28',    
//     created_at: '2025-12-28',
//     updated_at: '2025-12-28',
//   },
//   {
//     rule_id: '8',
//     rule_name: 'VIP Spactive_window_end_date Bonus 21',
//     rule_description: '#1021 - by rahul.m',
//     benefit_mechanic: 'prive',
//     mechanic: 'Discount + Gift',
//     area: 'Lifecycle Triggered',
//     storefront: 'UK',
//     priority: 2,
//     status: 'Paused',
//     active_window_start_date: '2025-12-28',
//     active_window_end_date: '2025-12-28',   
//     created_at: '2025-12-28',
//     updated_at: '2025-12-28',
//   },
//   {
//     rule_id: '9',
//     rule_name: 'Black Friday Discount 12',
//     rule_description: '#1012 - by admin',
//     benefit_mechanic: 'Member',
//     mechanic: 'Discount',
//     area: 'Lifecycle Triggered',
//     storefront: 'UK',
//     priority: 4,
//     status: 'Draft',
//     active_window_start_date: '2025-12-28',
//     active_window_end_date: '2025-12-28',   
//     created_at: '2025-12-28',
//     updated_at: '2025-12-28',
//   },
//   {
//     rule_id: '10',
//     rule_name: 'First Purchase GWP 15',
//     rule_description: '#1015 - by admin',
//     benefit_mechanic: 'Member',
//     mechanic: 'Gift',
//     area: 'BAU',
//     storefront: 'DE',
//     priority: 5,
//     status: 'Active',
//     active_window_start_date: '2025-12-28',
//     active_window_end_date: '2025-12-28',    
//     created_at: '2025-12-28',
//     updated_at: '2025-12-28',
//   },
// ];

export const mapRuleToTableRow = (item) => ({
  key: item.rule_id,
  ruleId: item.rule_id,
  // Preserve the complete API object for View, Edit, and Duplicate actions.
  ruleData: item,
  ruleName: item.rule_name,
  description: item.rule_description,
  customerGroup: item.customer_group || item.benefit_type || 'Member',
  mechanic: item.mechanic || item.benefit_mechanic || 'Discount',
  area: item.area,
  store: item.storefront || item.store || 'US',
  priority: item.priority,
  status: item.status,
  start: item.active_window_start_date,
  end: item.active_window_end_date,
  uses: item.redemption_limit || 0,
  updated: item.updated_at || item.created_at,
});

// "20.00" -> "20"; a real fraction like "20.5" is kept so the user sees it and fixes it.
const toWholeNumberText = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const num = Number(value);
  return Number.isInteger(num) ? String(num) : String(value);
};

export const mapRuleToFormData = (rule = {}, defaults = {}) => {
  // Translate API naming variants into the wizard's form field names.
  const getValue = (...keys) => {
    const key = keys.find((candidate) => rule[candidate] !== undefined && rule[candidate] !== null);
    return key ? rule[key] : undefined;
  };
  const mechanic = String(getValue('mechanic', 'benefit_mechanic') || '').toLowerCase().replace(/[_\s+]/g, '');
  const giftEnabled = getValue('giftEnabled', 'gift_enabled');
  const discountEnabled = getValue('discountEnabled', 'discount_enabled');
  const area = String(getValue('area') || '').toLowerCase();
  const ruleType = getValue('ruleType') ?? (area ? (area === 'lifecycle_triggered' ? 'Lifecycle Triggered' : 'BAU') : defaults.ruleType);
  const benefitType = String(getValue('benefit_type', 'customer_group') || '').toLowerCase();
  const memberType = getValue('memberType') ?? (benefitType ? (benefitType === 'prive' ? 'Prive' : 'Member') : defaults.memberType);
  const lifecycleDays = getValue('triggerWindowDays', 'lifecycle_window_days', 'trigger_window_days');
  const lifecycleType = String(getValue('lifecycle_type') || '');
  const inclusionSkus = rule.inclusion?.product_skus;
  const exclusionSkus = rule.exclusion?.product_skus;
  const redemptionLimitType = getValue('redemption_limit_type');

  return {
    ...defaults,
    ruleName: getValue('ruleName', 'rule_name') ?? defaults.ruleName,
    storefront: getValue('storefront', 'store') ?? defaults.storefront,
    ruleDescription: getValue('ruleDescription', 'short_desc', 'rule_description') ?? defaults.ruleDescription,
    giftEnabled: giftEnabled ?? (mechanic ? mechanic.includes('gift') : defaults.giftEnabled),
    minCartValue: toWholeNumberText(getValue('minCartValue', 'threshold_min', 'min_cart_value')) ?? defaults.minCartValue,
    giftSkus: (() => {
      const gifts = getValue('giftSkus', 'gifts', 'gift_skus', 'giftSkuList', 'gift_sku_list');
      // API returns gifts as [{ sku }]; the form keeps plain SKU strings.
      return Array.isArray(gifts) ? gifts.map((gift) => (gift && typeof gift === 'object' ? gift.sku : gift)) : gifts ?? defaults.giftSkus;
    })(),
    giftsPerOrder: String(getValue('giftsPerOrder', 'gifts_per_rule', 'gifts_per_order') ?? defaults.giftsPerOrder),
    discountEnabled: discountEnabled ?? (mechanic ? mechanic.includes('discount') : defaults.discountEnabled),
    discountType: (() => {
      const discountType = getValue('discountType', 'discount_type');
      return discountType === 'fixed_amount' ? 'fixed' : discountType ?? defaults.discountType;
    })(),
    discountValue: toWholeNumberText(getValue('discountValue', 'discount_value')) ?? defaults.discountValue,
    ruleType,
    memberType,
    activeStartDate: getValue('activeStartDate', 'active_window_start_date') ?? defaults.activeStartDate,
    activeEndDate: getValue('activeEndDate', 'active_window_end_date') ?? defaults.activeEndDate,
    triggerWindowDays: lifecycleDays != null ? String(lifecycleDays) : defaults.triggerWindowDays,
    ...(lifecycleType && {
      triggerWindows: [{
        id: 'default-trigger-window',
        days: lifecycleDays != null ? String(lifecycleDays) : '',
        field: lifecycleType.charAt(0).toUpperCase() + lifecycleType.slice(1).toLowerCase(),
        operator: '=',
        value: '',
      }],
    }),
    RedemptionType: getValue('RedemptionType')
      ?? (getValue('lifecycle_redemption_type') === 'once_per_year' ? 'Once Per Year' : 'One Time'),
    eligibilityGroups: getValue('eligibilityGroups', 'eligibility_groups')
      ?? mapConditionsToEligibilityGroups(rule.conditions)
      ?? defaults.eligibilityGroups,
    attributeCondition: getValue('attributeCondition', 'attribute_condition') ?? defaults.attributeCondition,
    redemptionLimit: String(getValue('redemptionLimit', 'redemption_limit') ?? (redemptionLimitType === 'unlimited' ? '' : defaults.redemptionLimit)),
    redemptionLimitUnlimited: getValue('redemptionLimitUnlimited', 'redemption_limit_unlimited') ?? redemptionLimitType === 'unlimited',
    priority: String(getValue('priority') ?? defaults.priority),
    listType: getValue('listType', 'list_type') ?? (rule.exclusion?.product_skus ? 'Excluded' : defaults.listType),
    skuSource: getValue('skuSource', 'sku_source') ?? defaults.skuSource,
    includedSkus: getValue('includedSkus', 'included_skus') ?? inclusionSkus ?? defaults.includedSkus,
    excludedSkus: getValue('excludedSkus', 'excluded_skus') ?? exclusionSkus ?? defaults.excludedSkus,
  };
};

export default mapRuleToTableRow;
